// src/view/DMap/SitePreviewMap.tsx
import React, { useRef, useEffect, useState } from 'react';
import mapboxgl, { Map } from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { mapBoxConfig } from '../../mapbox-helpers/config';
import GeoJSON from 'geojson';


// Set Mapbox access token
if (mapBoxConfig.accessToken) {
    mapboxgl.accessToken = mapBoxConfig.accessToken;
} else {
    console.error('Mapbox access token is not configured in mapBoxConfig.');
}

export interface PreviewPoint {
    name: string;
    lat: number;
    long: number;
    value?: number;
    type?: string;
}

interface SitePreviewMapProps {
    points: PreviewPoint[];
    drawType?: 'marker' | 'circle';
}

const SitePreviewMap: React.FC<SitePreviewMapProps> = ({ points, drawType = 'marker' }) => {
    const mapContainer = useRef<HTMLDivElement | null>(null);
    const mapRef = useRef<Map | null>(null);
    const markersRef = useRef<mapboxgl.Marker[]>([]);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    // Effect to initialize the map
    useEffect(() => {
        if (!mapContainer.current) return;

        // Initialize map
        const map = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/mapbox/streets-v12',
            center: [mapBoxConfig.defaulView.lng, mapBoxConfig.defaulView.lat],
            zoom: 4,
        });
        mapRef.current = map;

        map.addControl(new mapboxgl.NavigationControl(), 'top-right');

        map.on('load', () => {
            const sourceId = 'csv-points-source';
            const layerId = 'csv-points-layer';

            map.addSource(sourceId, {
                type: 'geojson',
                data: {
                    type: 'FeatureCollection',
                    features: []
                }
            });

            map.addLayer({
                id: layerId,
                type: 'circle',
                source: sourceId,
                paint: {
                    'circle-radius': 6,
                    'circle-color': '#2E8B57', // SeaGreen - a nature-related color
                    'circle-stroke-width': 1,
                    'circle-stroke-color': '#ffffff'
                },
                layout: {
                    visibility: 'none' // Initially hidden
                }
            });

            const popup = new mapboxgl.Popup({
                closeButton: false,
                closeOnClick: false
            });

            map.on('mouseenter', layerId, (e) => {
                map.getCanvas().style.cursor = 'pointer';
                const feature = e.features?.[0];
                if (feature?.geometry.type === 'Point') {
                    const coordinates = feature.geometry.coordinates.slice();
                    const properties = feature.properties;

                    if (properties) {
                        const description = `<strong>${properties.name}</strong><p>${properties.type}</p><p>Value: ${properties.value}</p>`;
                        // Ensure that if the map is zoomed out such that multiple
                        // copies of the feature are visible, the popup appears
                        // over the copy being pointed to.
                        while (Math.abs(e.lngLat.lng - coordinates[0]) > 180) {
                            coordinates[0] += e.lngLat.lng > coordinates[0] ? 360 : -360;
                        }
                        popup.setLngLat(coordinates as [number, number]).setHTML(description).addTo(map);
                    }
                }
            });

            map.on('mouseleave', layerId, () => {
                map.getCanvas().style.cursor = '';
                popup.remove();
            });
            setIsMapLoaded(true);
        });

        return () => {
            map.remove();
            mapRef.current = null;
        };
    }, []); // Run only once on mount

    // Effect to update data and fit bounds
    useEffect(() => {
        const map = mapRef.current;
        if (!map || !isMapLoaded || !points) return;

        const sourceId = 'csv-points-source';
        const layerId = 'csv-points-layer';

        // Cleanup previous markers
        markersRef.current.forEach(marker => marker.remove());
        markersRef.current = [];

        if (drawType === 'marker') {
            // Hide circle layer
            if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', 'none');

            // Add markers
            const newMarkers = points.map(p => {
                const popup = new mapboxgl.Popup({ offset: 25 })
                    .setHTML(`<strong>${p.name}</strong><p>${p.type}</p><p>Value: ${p.value}</p>`);

                return new mapboxgl.Marker({ color: '#2E8B57' })
                    .setLngLat([p.long, p.lat])
                    .setPopup(popup)
                    .addTo(map);
            });
            markersRef.current = newMarkers;
        } else { // 'circle'
            // Show circle layer
            if (map.getLayer(layerId)) map.setLayoutProperty(layerId, 'visibility', 'visible');

            // Update source data
            const source = map.getSource(sourceId) as mapboxgl.GeoJSONSource;
            if (source) {
                const features: GeoJSON.Feature<GeoJSON.Point>[] = points.map((p) => ({
                    type: 'Feature',
                    geometry: { type: 'Point', coordinates: [p.long, p.lat] },
                    properties: { name: p.name, value: p.value, type: p.type }
                })) as GeoJSON.Feature<GeoJSON.Point>[];
                const geoJsonSource: GeoJSON.FeatureCollection<GeoJSON.Point> = {
                    type: 'FeatureCollection',
                    features: features
                };
                source.setData(geoJsonSource);
            }
        }

        // Fit bounds logic (works for both markers and circles)
        if (points.length > 0) {
            if (points.length === 1) {
                map.flyTo({ center: [points[0].long, points[0].lat], zoom: 10, animate: true, duration: 1500 });
            } else {
                // Use LngLatBounds to calculate the bounding box, which is more robust
                const bounds = new mapboxgl.LngLatBounds();
                points.forEach(point => {
                    bounds.extend([point.long, point.lat]);
                });
                map.fitBounds(bounds, { padding: 60, animate: true, duration: 1500 });
            }
        }
    }, [points, drawType, isMapLoaded]); // Re-run when points data or drawType changes

    return <div ref={mapContainer} style={{ position: 'absolute', top: 0, bottom: 0, width: '100%' }} />;
};

export default SitePreviewMap;
